/**
 * Görev üreticileri.
 *
 * Her görev literatürde yerleşik bir PARADİGMA üzerine kuruludur. Paradigma
 * kamuya açıktır; telifli olan şey belirli bir yayıncının somut test
 * formudur. Bu yüzden kendi uyarıcı setimizi üretiyoruz ve hiçbir yerde
 * başka bir testin maddelerini kullanmıyoruz.
 *
 * Hepsi saf: aynı tohum + aynı seviye = aynı deneme. Yeniden üretilebilirlik
 * hem egzersiz basmak hem ölçümü sonradan incelemek için gerekli.
 */

import { Rng } from './prng'
import { accuracyScore, rtScores, signalScores, thresholdFromLevels, round } from './scoring'
import type { ScoreRow, TaskCode, TaskDefinition, TrialRecord, TrialSpec } from './types'

/** Harf havuzu: Türkçede birbirine karışmayan, tek heceli okunur harfler */
const LETTERS = ['A', 'B', 'E', 'F', 'K', 'L', 'M', 'N', 'R', 'S', 'T', 'Z'] as const
const SHAPES = ['daire', 'kare', 'ucgen', 'yildiz', 'altigen', 'kalp'] as const
const COLORS = ['mavi', 'kirmizi', 'yesil', 'sari'] as const

// =====================================================================
// 1) CPT — sürdürülen dikkat + ketleme
//
// Uyarıcılar akar; çocuk hedefe tepki verir, hedef olmayana vermez.
// Hedef oranı kasten DÜŞÜK (%25): böylece tepki vermek alışkanlık olur ve
// "vermeme" gerçekten ketleme gerektirir. Yüksek hedef oranı dürtü
// kontrolünü ölçmez.
// =====================================================================
const cpt: TaskDefinition = {
  code: 'cpt',
  domains: ['sustained_attention', 'inhibition'],
  trials: 80,
  adaptive: false,
  generate({ index, level, rngSeed }) {
    const rng = new Rng(rngSeed + index * 7919)
    const isTarget = rng.bool(0.25)
    const letter = isTarget ? 'X' : rng.pick(LETTERS)
    /**
     * Seviye yükseldikçe uyarıcı kısalır ve aralık daralır: zorluk
     * dikkatin sürdürülmesinden gelir.
     *
     * Başlangıç süresi bilerek uzun. İlk hali 285 ms ile başlıyordu ve
     * sahada "X çok hızlı geçiyor" diye geri geldi — yetişkin için
     * standart bir süre, görevi ilk kez gören bir çocuk için değil.
     * Çocuk önce ne arayacağını öğrenmeli; hız sonra gelir.
     */
    /**
     * Uyarıcı, tepki penceresinin büyük bölümünde ekranda kalıyor.
     *
     * Klasik CPT'de harf 250 ms görünüp kaybolur; çocuk bastığında ekran
     * çoktan boştur ve basışının neye ait olduğunu göremez. Burada harf
     * duruyor, basınca etrafında halka çıkıyor. Bu, görevi algısal hız
     * görevi olmaktan uzaklaştırıp dikkatin sürdürülmesine ve dürtü
     * kontrolüne yaklaştırıyor — çocukta ölçmek istediğimiz de bu.
     */
    const durationMs = Math.max(320, 900 - level * 60)
    const isiMs = Math.max(550, 1500 - level * 80)
    return {
      index,
      task: 'cpt',
      domain: 'sustained_attention',
      level,
      isTarget,
      expect: isTarget ? 'respond' : 'withhold',
      durationMs,
      isiMs,
      responseWindowMs: durationMs + isiMs,
      stimulus: { kind: 'letter', letter },
    } satisfies TrialSpec
  },
  score(trials) {
    return [
      ...signalScores(trials, 'sustained_attention'),
      ...rtScores(trials, 'sustained_attention'),
    ]
  },
}

// =====================================================================
// 2) Flanker — seçici dikkat
//
// Ortadaki oka cevap verilir, yanındakiler çeldirici. Uyumsuz denemede
// çeldiriciler ters yöne bakar. Asıl ölçüt tek bir süre değil, uyumsuz
// ile uyumlu arasındaki FARK: çeldiriciyi bastırmanın bedeli.
// =====================================================================
const flanker: TaskDefinition = {
  code: 'flanker',
  domains: ['selective_attention'],
  trials: 60,
  adaptive: false,
  generate({ index, level, rngSeed }) {
    const rng = new Rng(rngSeed + index * 6271)
    const congruent = rng.bool(0.5)
    const dir = rng.pick(['sol', 'sag'] as const)
    const flank = congruent ? dir : dir === 'sol' ? 'sag' : 'sol'
    // Seviye: çeldirici sayısı ve sunum süresi
    const flankerCount = Math.min(8, 2 + Math.floor(level / 2))
    const durationMs = Math.max(220, 700 - level * 50)
    return {
      index,
      task: 'flanker',
      domain: 'selective_attention',
      level,
      isTarget: true,
      expect: 'choice',
      durationMs,
      isiMs: 700,
      // Çocuk düğmeye uzanacak: yetişkin ölçümlerindeki 1500 ms dar kalıyor
      responseWindowMs: 2500,
      correct: dir,
      stimulus: { kind: 'flanker', dir, flank, flankerCount, congruent },
    } satisfies TrialSpec
  },
  score(trials) {
    const rows: ScoreRow[] = [
      ...accuracyScore(trials, 'selective_attention'),
      ...rtScores(trials, 'selective_attention'),
    ]
    // Çeldirici bedeli: uyumsuz - uyumlu ortalama süre
    const valid = trials.filter((t) => t.valid && t.correct && t.rtMs !== null)
    const inc = valid.filter((t) => t.stimulus['congruent'] === false).map((t) => t.rtMs!)
    const con = valid.filter((t) => t.stimulus['congruent'] === true).map((t) => t.rtMs!)
    if (inc.length >= 5 && con.length >= 5) {
      const cost = avg(inc) - avg(con)
      rows.push({
        domain: 'selective_attention',
        metric: 'threshold',
        value: round(cost, 1),
        unit: 'ms',
        trialsUsed: inc.length + con.length,
      })
    }
    return rows
  },
}

// =====================================================================
// 3) Görsel-uzamsal dizi — işleyen bellek (görsel)
//
// Izgarada hücreler sırayla yanar, çocuk aynı sırayı tekrarlar.
// UYARLANABİLİR: seviye = dizi uzunluğu. Çıktı "kaç basamak" eşiği.
// =====================================================================
const spanVisual: TaskDefinition = {
  code: 'span_visual',
  domains: ['working_memory_visual'],
  trials: 30,
  adaptive: true,
  generate({ index, level, rngSeed }) {
    const rng = new Rng(rngSeed + index * 4523)
    const gridSize = 4
    const cells = Array.from({ length: gridSize * gridSize }, (_, i) => String(i))
    // Ardışık komşu hücre seçmemeye çalışmıyoruz: komşuluk ipucu olabilir
    // ama rastgelelik daha saf bir bellek yükü verir
    const sequence = rng.sample(cells, Math.max(2, level))
    return {
      index,
      task: 'span_visual',
      domain: 'working_memory_visual',
      level,
      isTarget: true,
      expect: 'sequence',
      durationMs: 600 * sequence.length,
      isiMs: 500,
      responseWindowMs: 3000 + 1200 * sequence.length,
      correct: sequence,
      stimulus: { kind: 'grid_sequence', gridSize, sequence, flashMs: 500 },
    } satisfies TrialSpec
  },
  score(trials) {
    const th = thresholdFromLevels(trials)
    return [
      {
        domain: 'working_memory_visual',
        metric: 'span',
        value: round(th.value, 2),
        unit: 'span',
        trialsUsed: trials.filter((t) => t.valid).length,
      },
      ...accuracyScore(trials, 'working_memory_visual'),
    ]
  },
}

// =====================================================================
// 4) n-back — işleyen bellek (sözel)
//
// Harf akar; çocuk, n adım önceki harfle aynıysa tepki verir.
// UYARLANABİLİR: seviye = n. Hedef oranı %30 civarında tutulur, ve
// GEÇMİŞE BAKARAK üretilir — bu yüzden generate ctx.history alır.
// =====================================================================
const nback: TaskDefinition = {
  code: 'nback',
  domains: ['working_memory_verbal'],
  trials: 60,
  adaptive: true,
  generate({ index, level, rngSeed, history }) {
    const rng = new Rng(rngSeed + index * 3371)
    const n = Math.max(1, Math.min(4, level))
    const back = history[history.length - n]
    const canBeTarget = back !== undefined
    const isTarget = canBeTarget && rng.bool(0.3)

    let letter: string
    if (isTarget) {
      letter = String(back!.stimulus['letter'])
    } else {
      // Hedef olmayan denemede, n adım öncekiyle AYNI harfi vermemek gerek:
      // yoksa hedef olmayan deneme kazara hedef olur
      const forbidden = canBeTarget ? String(back!.stimulus['letter']) : null
      const pool = LETTERS.filter((l) => l !== forbidden)
      letter = rng.pick(pool)
    }

    return {
      index,
      task: 'nback',
      domain: 'working_memory_verbal',
      level: n,
      isTarget,
      expect: isTarget ? 'respond' : 'withhold',
      // Harf ekranda kalıyor; hatırlanması gereken şey ÖNCEKİ harfler,
      // şu an görünen değil
      durationMs: 1000,
      isiMs: 1500,
      responseWindowMs: 2500,
      stimulus: { kind: 'letter', letter, n },
    } satisfies TrialSpec
  },
  score(trials) {
    const th = thresholdFromLevels(trials)
    return [
      {
        domain: 'working_memory_verbal',
        metric: 'span',
        value: round(th.value, 2),
        unit: 'span',
        trialsUsed: trials.filter((t) => t.valid).length,
      },
      // n-back'te yanlış basma dürtüsellik değil bellek hatası:
      // kendi alanında kalıyor
      ...signalScores(trials, 'working_memory_verbal', 'working_memory_verbal'),
    ]
  },
}

// =====================================================================
// 5) Sembol arama — işlem hızı
//
// Anahtarda sembol-şekil eşleşmesi verilir, çocuk hedefi arar.
// Zorluk çeldirici benzerliğinden ve ızgara boyutundan gelir.
// =====================================================================
const symbolSearch: TaskDefinition = {
  code: 'symbol_search',
  domains: ['processing_speed'],
  trials: 48,
  adaptive: false,
  generate({ index, level, rngSeed }) {
    const rng = new Rng(rngSeed + index * 2857)
    const gridSize = Math.min(6, 3 + Math.floor(level / 3))
    const present = rng.bool(0.5)
    const target = rng.pick(SHAPES)
    const distractors = SHAPES.filter((s) => s !== target)
    const cellCount = gridSize * gridSize
    const items: string[] = Array.from({ length: cellCount }, () => rng.pick(distractors))
    if (present) items[rng.int(0, cellCount - 1)] = target
    return {
      index,
      task: 'symbol_search',
      domain: 'processing_speed',
      level,
      isTarget: present,
      expect: 'choice',
      durationMs: 0, // kendi hızında, süre sınırı yok
      isiMs: 400,
      responseWindowMs: 8000,
      correct: present ? 'var' : 'yok',
      stimulus: { kind: 'symbol_search', gridSize, target, items },
    } satisfies TrialSpec
  },
  score(trials) {
    return [...accuracyScore(trials, 'processing_speed'), ...rtScores(trials, 'processing_speed')]
  },
}

// =====================================================================
// 6) Kural değiştirme — bilişsel esneklik
//
// Bazı bloklarda renge, bazılarında şekle göre cevap verilir. Kural
// değiştiği deneme ile değişmediği deneme arasındaki süre farkı
// "geçiş bedeli": esnekliğin asıl ölçütü bu.
// =====================================================================
const switching: TaskDefinition = {
  code: 'switching',
  domains: ['cognitive_flexibility'],
  trials: 64,
  adaptive: false,
  generate({ index, level, rngSeed, history }) {
    const rng = new Rng(rngSeed + index * 1931)
    const prev = history[history.length - 1]
    const prevRule = prev ? String(prev.stimulus['rule']) : null
    // Seviye yükseldikçe kural daha sık değişir
    const switchProb = Math.min(0.6, 0.15 + level * 0.05)
    const rule =
      prevRule === null
        ? rng.pick(['renk', 'sekil'] as const)
        : rng.bool(switchProb)
          ? prevRule === 'renk'
            ? 'sekil'
            : 'renk'
          : prevRule
    const color = rng.pick(COLORS)
    const shape = rng.pick(SHAPES)
    return {
      index,
      task: 'switching',
      domain: 'cognitive_flexibility',
      level,
      isTarget: true,
      expect: 'choice',
      durationMs: Math.max(300, 900 - level * 40),
      isiMs: 500,
      responseWindowMs: 2500,
      correct: rule === 'renk' ? color : shape,
      stimulus: { kind: 'rule_switch', rule, color, shape, isSwitch: prevRule !== null && rule !== prevRule },
    } satisfies TrialSpec
  },
  score(trials) {
    const rows: ScoreRow[] = [
      ...accuracyScore(trials, 'cognitive_flexibility'),
      ...rtScores(trials, 'cognitive_flexibility'),
    ]
    const valid = trials.filter((t) => t.valid && t.correct && t.rtMs !== null)
    const sw = valid.filter((t) => t.stimulus['isSwitch'] === true).map((t) => t.rtMs!)
    const st = valid.filter((t) => t.stimulus['isSwitch'] === false).map((t) => t.rtMs!)
    if (sw.length >= 5 && st.length >= 5) {
      rows.push({
        domain: 'cognitive_flexibility',
        metric: 'threshold',
        value: round(avg(sw) - avg(st), 1),
        unit: 'ms',
        trialsUsed: sw.length + st.length,
      })
    }
    return rows
  },
}

function avg(xs: number[]): number {
  return xs.length === 0 ? 0 : xs.reduce((a, b) => a + b, 0) / xs.length
}

// =====================================================================
// 7) Ritim — zamanlama ve ardışık işlemleme
//
// Bir ritim çalınır, çocuk aynı ritmi dokunarak geri verir.
//
// BU GÖREV BATARYAYA SONRADAN EKLENDİ ve eklenmesi `INSTRUMENT_VERSION`
// 1.0 → 1.1 demek. Ölçümün yapısı ve uzunluğu değişti; 1.0 ile alınan
// sayılar 1.1 ile alınanlarla KARŞILAŞTIRILAMAZ. Rapor katmanı bunu
// biliyor ve iki ayrı sürümü karşılaştırmayı reddediyor (`rapor.ts`).
// Eski ölçümler silinmiyor, eski sürümle etiketli duruyor.
//
// DOĞRULUK TOLERANSLI ve ORAN üzerinden: aralıklar kendi ortalamalarına
// bölünerek karşılaştırılıyor, yani ritmi doğru ama yavaş tekrarlamak
// doğru sayılıyor. Mutlak milisaniyeye bakmak tempo ölçerdi, ritmi
// değil.
// =====================================================================
const rhythm: TaskDefinition = {
  code: 'rhythm',
  domains: ['processing_speed'],
  trials: 8,
  adaptive: false,
  generate({ index, level, rngSeed }) {
    const rng = new Rng(rngSeed + index * 5261)
    // Seviye yükseldikçe vuruş artıyor ve tolerans daralıyor
    const vurus = Math.min(7, 3 + Math.floor(level / 2))
    const degerler = level >= 4 ? [1, 2, 3] : [1, 2]
    const birim = 400

    /**
     * Desen tekdüze olamaz: bütün aralıklar aynıysa görev ritim
     * tekrarı değil, eşit aralıklarla dokunma olur.
     */
    let katlar: number[] = []
    for (let d = 0; d < 40; d++) {
      katlar = Array.from({ length: vurus - 1 }, () => rng.pick(degerler))
      if (new Set(katlar).size > 1) break
    }
    if (new Set(katlar).size < 2) katlar[0] = degerler[degerler.length - 1]!

    const araliklar = katlar.map((k) => k * birim)
    const tolerans = Math.max(0.15, 0.32 - level * 0.02)
    const toplam = araliklar.reduce((a, b) => a + b, 0)

    return {
      index,
      task: 'rhythm',
      domain: 'processing_speed',
      level,
      isTarget: true,
      expect: 'timing',
      // Uyarıcı ritmin kendisi: çalma süresi kadar ekranda
      durationMs: toplam + 400,
      isiMs: 600,
      /**
       * Tepki penceresi cömert: çocuk ritmi dinledikten SONRA
       * tekrarlıyor ve tekrar en az ritim kadar sürüyor. Dar bir
       * pencere, zamanlamayı değil aceleyi ölçerdi.
       */
      responseWindowMs: toplam * 3 + 4000,
      correct: araliklar.map(String),
      stimulus: { araliklar, tolerans, vurus },
    }
  },
  score(trials) {
    // Doğruluk: toleransın içinde kalan deneme oranı. Ayrı bir ölçüt
    // açmak rapor katmanını da değiştirirdi; oran zaten aranan şey.
    return accuracyScore(trials, 'processing_speed')
  },
}

export const TASKS = {
  cpt,
  flanker,
  span_visual: spanVisual,
  nback,
  symbol_search: symbolSearch,
  switching,
  rhythm,
} as const

export function getTask(code: keyof typeof TASKS): TaskDefinition {
  return TASKS[code]
}

export function scoreAll(trials: TrialRecord[]): ScoreRow[] {
  const byTask = new Map<string, TrialRecord[]>()
  for (const t of trials) {
    const list = byTask.get(t.task) ?? []
    list.push(t)
    byTask.set(t.task, list)
  }
  const rows: ScoreRow[] = []
  for (const [code, list] of byTask) {
    const task = TASKS[code as keyof typeof TASKS]
    if (!task) continue
    // Her satır hangi görevden geldiğini taşısın
    rows.push(...task.score(list).map((r) => ({ ...r, task: code as TaskCode })))
  }
  return rows
}
